/** Pure Coach rules shared by the browser and Worker; no network or storage access. */
export interface CoachTask {
  title?: unknown;
  status?: unknown;
  project?: unknown;
}

export interface CoachHistory {
  duration?: unknown;
  interruptions?: unknown;
  pausedSeconds?: unknown;
  rating?: unknown;
  mood?: unknown;
  createdAt?: unknown;
  sessionType?: unknown;
}

export interface CoachContextInput {
  tasks: readonly CoachTask[];
  history: readonly CoachHistory[];
}

const safeText = (value: unknown, maxLength: number, fallback = '') =>
  typeof value === 'string' ? value.trim().slice(0, maxLength) || fallback : fallback;

const nonNegative = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0;

const timestamp = (value: unknown) => {
  const parsed = typeof value === 'string' ? Date.parse(value) : NaN;
  return Number.isFinite(parsed) ? parsed : -Infinity;
};

export const selectRecentHistory = <T extends CoachHistory>(
  history: readonly T[],
  limit = 50,
): T[] =>
  [...history]
    .sort((a, b) => timestamp(b.createdAt) - timestamp(a.createdAt))
    .slice(0, Math.max(0, Math.floor(limit)));

/** Keep API context small, recent, and free of notes and record identifiers. */
export const buildCoachContext = ({ tasks, history }: CoachContextInput) => ({
  tasks: tasks
    .filter((task) => task.status !== 'done')
    .slice(0, 8)
    .map((task) => ({
      title: safeText(task.title, 160, 'Untitled task'),
      status: 'open',
      ...(typeof task.project === 'string' ? { project: safeText(task.project, 80) } : {}),
    })),
  history: selectRecentHistory(history, 50).map((entry) => ({
    duration: nonNegative(entry.duration),
    interruptions: nonNegative(entry.interruptions),
    pausedSeconds: nonNegative(entry.pausedSeconds),
    ...(typeof entry.rating === 'number' && Number.isFinite(entry.rating)
      ? { rating: entry.rating }
      : {}),
    ...(typeof entry.mood === 'string' ? { mood: safeText(entry.mood, 24) } : {}),
    ...(Number.isFinite(timestamp(entry.createdAt))
      ? { createdAt: safeText(entry.createdAt, 40) }
      : {}),
    ...(entry.sessionType === 'work' ||
    entry.sessionType === 'short_break' ||
    entry.sessionType === 'long_break'
      ? { sessionType: entry.sessionType }
      : {}),
  })),
});

export const buildHeuristicSuggestions = (
  tasks: readonly CoachTask[],
  history: readonly CoachHistory[],
) => {
  const context = buildCoachContext({ tasks, history });
  const focusMinutes = context.history
    .filter((entry) => entry.sessionType === 'work')
    .reduce((total, entry) => total + entry.duration, 0);
  const unknownMinutes = context.history
    .filter((entry) => !entry.sessionType)
    .reduce((total, entry) => total + entry.duration, 0);
  const workRecords = context.history.filter(
    (entry) => !entry.sessionType || entry.sessionType === 'work',
  );
  const interruptions = workRecords.reduce((total, entry) => total + entry.interruptions, 0);
  const firstTask = safeText(context.tasks[0]?.title, 96, 'your next task');
  const rounded = (minutes: number) => Math.round(minutes * 10) / 10;

  return [
    context.tasks.length
      ? `Start with "${firstTask}" and keep the first focus block narrow.`
      : 'Capture one concrete task before starting the next focus block.',
    focusMinutes > 0
      ? `Your recent records contain ${rounded(focusMinutes)} focus minutes; choose one clear outcome for the next block.`
      : unknownMinutes > 0
        ? `You have ${rounded(unknownMinutes)} recorded minutes without a session type; keep them separate from verified focus time.`
        : 'Start with a short focus block and review the result before planning more.',
    interruptions > workRecords.length
      ? 'Recent work records show repeated interruptions; try a smaller task and silence one distraction before starting.'
      : 'Keep the default break rhythm unless the next task needs a longer reset.',
  ];
};

export const buildTaskBreakdown = (task: string) => {
  const title = task.trim() || 'Clarify the outcome';
  return [
    `Define done for "${title}"`,
    'List the smallest next action',
    'Run one focused block',
    'Review and choose the next block',
  ];
};
